import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ChannelStrip } from "@/components/channels/ChannelStrip";
import PageHeader from "@/components/common/PageHeader";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface SuiteApp {
  name: string;
  productWord: string;
  genieIcon: GenieIconName;
  description: string;
  path: string;
  cta: string;
}

const SUITE_APPS: SuiteApp[] = [
  {
    name: "Help Genie",
    productWord: "Mail",
    genieIcon: "mail",
    description:
      "Answers your support email from your Genie's knowledge base, and escalates the rest to you.",
    path: "/genie-hub/channels/mail",
    cta: "Open Mail",
  },
  {
    name: "Help Genie",
    productWord: "Sync",
    genieIcon: "sync",
    description:
      "Connect HubSpot, Slack, and your other tools so Genies can act after every call. No copy-paste.",
    path: "/genie-hub/channels/sync",
    cta: "Open Sync",
  },
  {
    name: "Help Genie",
    productWord: "Outbound",
    genieIcon: "phone",
    description:
      "Place real calls and run campaigns. Your Genie dials, talks, and reports back with a transcript.",
    path: "/genie-hub/channels/outbound",
    cta: "Open Outbound",
  },
];

const SuiteAppCard = ({ app }: { app: SuiteApp }) => (
  <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] hover:shadow-xl hover:shadow-genie-primary/20 transition-all duration-300">
    <div className="rounded-[14px] bg-white p-6 md:p-7 h-full flex flex-col">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <GenieIcon
            name={app.genieIcon}
            size={40}
            className="h-10 w-10 md:h-11 md:w-11 flex-shrink-0"
          />
          <h2 className="text-xl md:text-2xl font-bold text-[#222B45] leading-tight">
            {app.name}{" "}
            <span className="italic bg-clip-text text-transparent bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
              {app.productWord}
            </span>
          </h2>
        </div>
      </div>

      <p className="text-sm md:text-base text-gray-600 mb-6 flex-1">
        {app.description}
      </p>

      <Link
        to={app.path}
        className="inline-flex items-center justify-center px-5 py-3 text-white rounded-xl font-semibold shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:opacity-95 transition-all duration-200 text-sm self-start"
        style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
      >
        {app.cta}
        <ArrowRight className="ml-2 w-4 h-4" />
      </Link>
    </div>
  </div>
);

const SuitePage = () => {
  return (
    <div className="min-h-screen bg-[#F9FAFC] overflow-x-hidden">
      <div className="container mx-auto px-4 py-8 max-w-[1920px]">
        <PageHeader
          title="Help Genie Suite"
          genieIcon="apps"
          subtitle="The apps your Genie works through. One login, one Genie."
        />

        {/* The five ways a customer reaches your Genie, and whether each works.
            Same component the dashboard REACH band uses — built once, used twice. */}
        <ChannelStrip title="Your channels" className="mb-10" />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {SUITE_APPS.map((app) => (
            <SuiteAppCard key={app.productWord} app={app} />
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8 max-w-3xl">
          <h3 className="text-base font-semibold text-gray-900 mb-2">
            What are Suite apps?
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed">
            Suite apps are the other places your Genie works. The Genie you build
            here answers your support email in Mail, acts on your tools through
            Sync, and places calls in Outbound. Same Genie, same knowledge, same
            login.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SuitePage;
